import { useMutation, useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Widget, WidgetContent, useCustomSnackbar } from '@procureai/ui-mui';
import { pascalCase } from 'change-case';
import { pick } from 'ramda';
import { useState, useMemo, useEffect, useRef } from 'react';

import { CmdiDataGridToolbar } from '../CmdiDataGridToolbar/CmdiDataGridToolbar';
import {
  AddRowButton,
  DataGridCustom,
  DataGridWidgetHeader,
  useDataGridRemoteState,
  useDataGridStatsTranslation,
} from '~/components/DataGridCustom';
import { useCmdiHasuraContext } from '~/hooks/useCmdiHasuraContext/useCmdiHasuraContext';

import { useCmdiDataGridActions } from './CmdiDataGridWidget.actions';
import { useCmdiDataGridConfig } from './CmdiDataGridWidget.config';
import { CmdiColumnConfig, CmdiRow } from './CmdiDataGridWidget.types';
import { getDeleteMutation, getMutation, getQuery } from './CmdiDataGridWidget.util';

export interface CmdiDataGridWidgetProps {
  entityName: string;
  columnConfig: CmdiColumnConfig[];
}

export const CmdiDataGridWidget = ({ columnConfig, entityName }: CmdiDataGridWidgetProps) => {
  const apiRef = useGridApiRef();
  const { onError } = useCustomSnackbar();
  const context = useCmdiHasuraContext();

  const [rows, setRows] = useState<CmdiRow[]>([]);

  const scrollToRef = useRef<string | null>(null);

  const columnNames = useMemo(() => {
    const arr = columnConfig.map((c) => c.columnName);
    return [...arr, 'id'];
  }, [columnConfig]);

  const { rowFactory, config } = useCmdiDataGridConfig(columnConfig);

  const domain: any = useMemo(() => `cmdi${pascalCase(entityName)}`, [entityName]);

  const { columns, rowModesModel, onRowModesModelChange, onRowAdd } = useCmdiDataGridActions({
    apiRef,
    columns: config.columns,
    onAdd: () => {
      const newRow = rowFactory();
      scrollToRef.current = newRow.id;
      setRows((prevRows) => [...prevRows, newRow]);
      return newRow;
    },
    onDelete: async (id) => {
      const { errors } = await remove({
        variables: { id },
      });
      if (!errors || errors.length === 0) {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      }
    },
    onCancel: (id) => {
      const row = apiRef.current.getRow(id);
      if (row?.isNew) {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      }
    },
  });

  const { loading: loadingRemoteState, gridProps } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const { query, mutation, deleteMutation } = useMemo(
    () => ({
      query: getQuery({ entityName, columnNames }),
      mutation: getMutation({ entityName, columnNames }),
      deleteMutation: getDeleteMutation({ entityName, columnNames }),
    }),
    [entityName, columnNames],
  );

  const { data, loading } = useQuery(query, {
    skip: loadingRemoteState,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    context,
    onError,
  });
  const [save] = useMutation(mutation, { onError, context });
  const [remove] = useMutation(deleteMutation, { onError, context });

  useEffect(() => {
    if (data) {
      setRows(data?.cmdi[entityName] || []);
    }
  }, [data, entityName]);

  useEffect(() => {
    setTimeout(() => {
      if (scrollToRef.current && apiRef.current) {
        const scrollToId = scrollToRef.current;
        scrollToRef.current = null;
        const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(scrollToId);
        apiRef.current.scrollToIndexes({ rowIndex });
      }
    }, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToRef.current]);

  const title = useDataGridStatsTranslation({ rowCount: rows.length || 0 });

  const processRowUpdate = async (newRow: CmdiRow) => {
    const updatedRow = { ...newRow, isNew: false };
    await save({ variables: { object: pick(columnNames, newRow) } });
    setRows((prevRows) => prevRows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return newRow;
  };

  return (
    <Widget>
      <DataGridWidgetHeader
        title={title}
        left={
          <Stack direction="row">
            <AddRowButton onAddClick={onRowAdd} />
          </Stack>
        }
      />
      <WidgetContent
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', overflow: 'hidden' }}
      >
        {loading || loadingRemoteState ? null : (
          <DataGridCustom
            sx={{
              // is needed to limit the height to the size of the parent flex-container
              // https://github.com/mui/mui-x/issues/8547
              overflow: 'hidden',
            }}
            {...gridProps}
            apiRef={apiRef}
            editMode="row"
            getRowId={(row) => row.id}
            rows={rows}
            rowCount={rows.length}
            columns={columns}
            rowModesModel={rowModesModel}
            onRowModesModelChange={onRowModesModelChange}
            processRowUpdate={processRowUpdate}
            initialState={{ pinnedColumns: { right: ['actions'] }, ...gridProps.initialState }}
            onProcessRowUpdateError={onError}
            disableColumnPinning={false}
            disableColumnMenu={false}
            disableColumnSelector={false}
            slots={{ toolbar: CmdiDataGridToolbar }}
            slotProps={{ baseButton: { variant: 'text' } }}
          />
        )}
      </WidgetContent>
    </Widget>
  );
};
